import { useState } from "react";
import ProductCard from "@/components/shop/ProductCard";
import ShopCartPill from "@/components/shop/ShopCartPill";
import { TOMORROW_COPY, tomorrowNames } from "@/data/tomorrow-co";
import type { ShopProduct } from "@/lib/printify";
import type { StoreCollection } from "@/lib/store-collections";
import theme from "@/styles/store-theme.module.css";
import TomorrowDesignTuner, { TOMORROW_TUNER_DEFAULTS, type TomorrowTunerState } from "./TomorrowDesignTuner";
import tokens from "./tomorrow-tokens.module.css";
import styles from "./TomorrowCollection.module.css";

/** Cards in the first row at desktop widths: their photos are above the fold. */
const FIRST_ROW = 4;

/** Tomorrow Co's branded collection page (/shop/tomorrow-co): dark store body with Tomorrow Co's
 *  palette as accents, its own hero and mission; its footer comes from LayoutDefault. */
export default function TomorrowCollection({ collection, products }: { collection: StoreCollection; products: ShopProduct[] }) {
  const names = tomorrowNames(collection.name);
  const [tuner, setTuner] = useState<TomorrowTunerState>(TOMORROW_TUNER_DEFAULTS);
  const tunerVars = {
    "--tc-hero-angle": `${tuner.heroAngle}deg`,
    "--tc-hero-glow": String(tuner.heroGlow),
    "--tc-section-gap": `${tuner.sectionGap}px`,
    "--tc-grid-gap": `${tuner.gridGap}px`,
  } as React.CSSProperties;
  return (
    <main className={`${theme.store} ${tokens.tc} ${styles.page}`} style={tunerVars}>
      <header className={styles.hero}>
        <span className={styles.eyebrow}>{names.eyebrow}</span>
        <h1 className={styles.title}>{TOMORROW_COPY.headline}</h1>
        <span className={styles.rule} aria-hidden="true" />
        <p className={styles.sub}>{TOMORROW_COPY.donation}</p>
        <div className={styles.actions}>
          <a href="#collection" className={styles.btnPrimary}>
            Shop the collection
          </a>
          <a href="#mission" className={styles.btnSecondary}>
            Our mission
          </a>
        </div>
        <p className={styles.disclaimer}>{TOMORROW_COPY.disclaimer}</p>
      </header>

      <section id="collection" aria-labelledby="collection-title" className={styles.section}>
        <h2 id="collection-title" className={styles.h2}>
          The collection
        </h2>
        {products.length > 0 ? (
          <div className={styles.grid}>
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} collectionName={collection.name} eager={i < FIRST_ROW} highPriority={i === 0} />
            ))}
          </div>
        ) : (
          <p className={styles.empty}>{TOMORROW_COPY.emptyState}</p>
        )}
      </section>

      <section id="mission" aria-labelledby="mission-title" className={`${styles.section} ${styles.mission}`}>
        <h2 id="mission-title" className={styles.h2}>
          Our mission
        </h2>
        <p className={styles.missionText}>{TOMORROW_COPY.mission}</p>
        <aside className={styles.callout}>{TOMORROW_COPY.callout}</aside>
      </section>

      <ShopCartPill />
      <TomorrowDesignTuner state={tuner} setState={setTuner} />
    </main>
  );
}
